import axios from 'axios';
import qs from 'qs';
import { EventTypes } from 'redux-segment';
import ActionTypes from '../../constants';

export type ValidateCardParams = {
  number: string;
  expireMonth: string;
  expireYear: string;
  type: string;
  cvv: string;
};

export type CardParams = {
  cvv: string;
  expiryMonth: string;
  expiryYear: string;
  issuer: string;
  name: string;
  number: string;
  defaultPayment: boolean;
  defaultAll: string;
  userId: string;
};

export type DirectDebitParams = {
  account: string;
  accountName: string;
  bsb: string;
  country: string;
  currency: string;
  defaultAll: boolean;
  defaultPayment: boolean;
  terms: boolean;
  type: string;
  userId: string;
};

export type PaymentMethod = {
  account: string;
  accountName: string;
  bankConfigured: boolean;
  bsb: string;
  cardConfigured: boolean;
  cardLast4Digits: string;
  cardName: string;
  cardType: string;
  createdAt: string;
  displayName: string;
  id: string;
  isCard: boolean;
  isDirect: boolean;
  maskedNumber: string;
  name: string;
  title: string;
  type: string;
  updatedAt: string;
};

const validateCardAsync = (cardDetails: ValidateCardParams) => dispatch =>
  dispatch({
    type: ActionTypes.VALIDATE_CARD,
    payload: axios({
      url: '/v1/payment-methods/validate',
      data: cardDetails,
      method: 'POST',
      responseType: 'json',
      timeout: 20000,
    }),
  });

const clearCardValidations = () => dispatch => {
  dispatch({
    type: ActionTypes.CLEAR_CARD_VALIDATIONS,
  });
};

const getCardTypeAsync = (cardNumber: string) => dispatch =>
  dispatch({
    type: ActionTypes.GET_CARD_TYPE,
    payload: axios({
      url: '/v1/payment-methods/card-type/',
      method: 'POST',
      data: { cardNumber },
      responseType: 'json',
      timeout: 20000,
    }),
  });

const addNewCard =
  (cardDetails, retailerId, paymentRequestId, token) => dispatch => {
    let url = `/v1/retailers/${retailerId}/payment-methods`;
    if (paymentRequestId && token) {
      const qst = qs.stringify({ token });
      url = `/v1/payment-requests/${paymentRequestId}?${qst}`;
    }
    return dispatch({
      type: ActionTypes.ADD_NEW_CARD,
      meta: {
        analytics: {
          eventType: EventTypes.track,
          eventPayload: {
            event: ActionTypes.DEBIT_CREDIT_CARD_SAVE_CLICKED,
          },
        },
      },
      payload: axios({
        url,
        method: 'POST',
        data: cardDetails,
        responseType: 'json',
        timeout: 20000,
      })
        .then(res => {
          dispatch({
            type: ActionTypes.NOTIFY_ADD_NEW_CARD_SUCCESS,
            notificationPayload: 'New card has been added',
            notificationDelay: ActionTypes.NOTIFICATION_DELAY,
            notificationType: 'success',
            meta: {
              analytics: {
                eventType: EventTypes.track,
                eventPayload: {
                  event: ActionTypes.ADD_NEW_CARD_SUCCESS,
                  properties: {
                    type: 'card',
                  },
                },
              },
            },
          });
          return Promise.resolve(res);
        })
        .catch(rej => {
          dispatch({
            type: ActionTypes.NOTIFY_ADD_NEW_CARD_FAILURE,
            notificationPayload: 'Failed to add new card',
            notificationDelay: ActionTypes.NOTIFICATION_DELAY,
            notificationType: 'error',
          });
          return Promise.reject(rej);
        }),
    });
  };

const addNewDirect =
  (directDetails, retailerId, paymentRequestId, token) => dispatch => {
    let url = `/v1/retailers/${retailerId}/payment-methods`;
    if (paymentRequestId && token) {
      const qst = qs.stringify({ token });
      url = `/v1/payment-requests/${paymentRequestId}?${qst}`;
    }
    return dispatch({
      type: ActionTypes.ADD_NEW_DIRECT_DEBIT,
      payload: axios({
        url,
        method: 'POST',
        data: directDetails,
        responseType: 'json',
        timeout: 20000,
      })
        .then(res => {
          dispatch({
            type: ActionTypes.NOTIFY_ADD_NEW_DIRECT_SUCCESS,
            notificationPayload: 'New account has been added',
            notificationDelay: ActionTypes.NOTIFICATION_DELAY,
            notificationType: 'success',
            meta: {
              analytics: {
                eventType: EventTypes.track,
                eventPayload: {
                  event: ActionTypes.ADD_NEW_DIRECT_SUCCESS,
                  properties: {
                    type: 'direct',
                  },
                },
              },
            },
          });
          return Promise.resolve(res);
        })
        .catch(rej => {
          dispatch({
            type: ActionTypes.NOTIFY_ADD_NEW_DIRECT_FAILURE,
            notificationPayload: 'Failed to add new account',
            notificationDelay: ActionTypes.NOTIFICATION_DELAY,
            notificationType: 'error',
          });
          return Promise.reject(rej);
        }),
    });
  };

const deletePaymentAsync = (id, type, retailerId) => dispatch =>
  dispatch({
    type: ActionTypes.DELETE_PAYMENT_METHOD,
    payload: axios({
      url: `/v1/retailers/${retailerId}/payment-methods/${id}`,
      method: 'DELETE',
      responseType: 'json',
      timeout: 20000,
    })
      .then(res => {
        dispatch({
          type: ActionTypes.NOTIFY_DELETE_PAYMENT_METHOD_SUCCESS,
          notificationPayload: `${type} has been removed`,
          notificationDelay: ActionTypes.NOTIFICATION_DELAY,
          notificationType: 'success',
        });
        return Promise.resolve(res);
      })
      .catch(rej => {
        dispatch({
          type: ActionTypes.NOTIFY_DELETE_PAYMENT_METHOD_FAILURE,
          notificationPayload: `Failed to remove ${type}`,
          notificationDelay: ActionTypes.NOTIFICATION_DELAY,
          notificationType: 'error',
        });
        return Promise.reject(rej);
      }),
  });

const fetchPaymentsAsync = retailerId => dispatch =>
  dispatch({
    type: ActionTypes.FETCH_PAYMENTS,
    payload: axios({
      url: `/v2/retailers/${retailerId}/payment-methods`,
      method: 'GET',
      responseType: 'json',
      timeout: 20000,
    }),
  });

const deletePaymentMethodAsync =
  ({ id, retailerId, type }) =>
  dispatch =>
    dispatch({
      type: ActionTypes.DELETE_PAYMENT_METHOD,
      payload: axios({
        url: `/v1/retailers/${retailerId}/payment-methods/${id}`,
        method: 'DELETE',
        responseType: 'json',
        timeout: 20000,
      })
        .then(res => {
          dispatch({
            type: ActionTypes.NOTIFY_DELETE_PAYMENT_METHOD_SUCCESS,
            notificationPayload: `${type} has been removed`,
            notificationDelay: ActionTypes.NOTIFICATION_DELAY,
            notificationType: 'success',
          });
          return Promise.resolve(res);
        })
        .catch(rej => {
          dispatch({
            type: ActionTypes.NOTIFY_DELETE_PAYMENT_METHOD_FAILURE,
            notificationPayload: `Failed to remove ${type}`,
            notificationDelay: ActionTypes.NOTIFICATION_DELAY,
            notificationType: 'error',
          });
          return Promise.reject(rej);
        }),
    });

export {
  validateCardAsync,
  getCardTypeAsync,
  addNewCard,
  addNewDirect,
  clearCardValidations,
  fetchPaymentsAsync,
  deletePaymentMethodAsync,
  deletePaymentAsync,
};
